import { useEffect } from 'react';
import {
  DefaultNodeOptions,
  StringNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { dateToMantineDateString } from '../../../components/datepicker/helper/mantineDateStrings';
import { FormField } from '../../../components/formfield/FormField';
import {
  isMonthEndIsoDate,
  MonthQuarterPicker,
} from '../../../components/month-quarter-picker/MonthQuarterPicker';
import { getDateFromJsonRef, useErrorTransformation } from '../widgetUtils';

export type MonthQuarterPickerOptions = DefaultNodeOptions<{
  clearable?: boolean;
  loading?: boolean;
  tableLayout?: boolean;
  showErrors?: boolean;
  showErrorWithColor?: boolean;
  maxDate?: string;
  maxDateRef?: string;
  widget?: 'MonthQuarterPicker';
}>;

export const MonthQuarterPickerWidget = widget<
  StringNode<MonthQuarterPickerOptions>,
  string | null
>(({ node, options, setValue, editor }) => {
  useEffect(() => {
    if (options.readOnly) {
      return;
    }

    if (node.value == null || node.value === '') {
      setValue(null);
      return;
    }

    if (!isMonthEndIsoDate(node.value)) {
      setValue(null);
    }
  }, [node.value, options.readOnly, setValue]);

  const errors = useErrorTransformation(node.errors);
  const hasError = node.errors.length > 0;
  const showError = options.showErrorWithColor || options.showErrors !== false;

  const maxDateFromRef =
    options?.maxDateRef != null
      ? getDateFromJsonRef(options.maxDateRef, editor.root, node)
      : undefined;
  const maxDate =
    options?.maxDate ??
    (maxDateFromRef != null
      ? dateToMantineDateString(maxDateFromRef)
      : undefined);

  return (
    <FormField
      widgetType="select"
      node={node}
      errors={errors}
      options={options}
      tableLayout={options.tableLayout}
    >
      <MonthQuarterPicker
        value={node.value ?? null}
        description={options.description}
        label={options.title}
        required={options.required}
        disabled={options.disabled}
        loading={options.loading}
        readOnly={options.readOnly}
        error={showError && hasError}
        clearable={options.clearable}
        defaultYearToCurrent={false}
        maxDate={maxDate}
        onChange={setValue}
      />
    </FormField>
  );
});

export const MonthQuarterPickerWidgetPlugin: WidgetPlugin = {
  id: 'month-quarter-picker-widget',
  use: (node) =>
    node.schema.options?.widget === 'MonthQuarterPicker' &&
    node.schema.format === 'date',
  Widget: MonthQuarterPickerWidget,
};
